import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import useTrainingCourse from '../../Hooks/useTrainingCourse';
import QuestionDisplay from '../../Components/TrainingCourse/QuestionDisplay';
import DataLoader from '../../Components/DataLoader';
import SubmitLoader from '../../Components/SubmitLoader';
import AlertMessage from '../../Components/AlertMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faCheckCircle,
  faCalendarAlt,
  faQuestionCircle,
  faTrophy,
  faTimesCircle,
  faExclamationTriangle,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import './CompetencyQuiz.css';

const CompetencyQuiz = () => {
  const { competencyAssessmentId } = useParams();
  const navigate = useNavigate();
  
  const {
    currentAssessment,
    candidateQuestions,
    evaluateAssessmentResult,
    candidateQuestionsLoading,
    candidateQuestionsError,
    assessmentLoading,
    assessmentError,
    fetchAssessment,
    fetchCandidateQuestions,
    evaluateCompetencyAssessment,
    evaluateAssessmentLoading,
    evaluateAssessmentError,
  } = useTrainingCourse();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const storageKey = `quiz_question_index_${competencyAssessmentId}`;
    const savedIndex = localStorage.getItem(storageKey);
    return savedIndex ? parseInt(savedIndex, 10) : 0;
  });
  const [answers, setAnswers] = useState(() => {
    const storageKey = `quiz_answers_${competencyAssessmentId}`;
    const savedAnswers = localStorage.getItem(storageKey);
    return savedAnswers ? JSON.parse(savedAnswers) : {};
  });
  const [showResults, setShowResults] = useState(false);
  const [quizStartTime, setQuizStartTime] = useState(new Date());
  const [quizEndTime, setQuizEndTime] = useState(null);
  const [totalTimeAllowed, setTotalTimeAllowed] = useState(0);
  const [totalTimeSpent, setTotalTimeSpent] = useState(() => {
    // Initialize from localStorage or start at 0
    const storageKey = `quiz_total_time_${competencyAssessmentId}`;
    const savedTime = localStorage.getItem(storageKey);
    return savedTime ? parseInt(savedTime, 10) : 0;
  });
  const [timerInterval, setTimerInterval] = useState(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isRetryMode, setIsRetryMode] = useState(() => {
    const storageKey = `quiz_retry_mode_${competencyAssessmentId}`;
    return localStorage.getItem(storageKey) === 'true';
  });
  const [resultsTimerHandled, setResultsTimerHandled] = useState(false);
  const [resultsTimeSpent, setResultsTimeSpent] = useState(0);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const isSubmittedRef = useRef(false);
  const showResultsRef = useRef(false);

  // React Hook Form setup
  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      competencyAssessmentId: parseInt(competencyAssessmentId),
      candidateMinutesToComplete: 0,
      questionAnswers: []
    }
  });

  // Fetch candidate questions on component mount
  useEffect(() => {
    if (competencyAssessmentId) {
      fetchCandidateQuestions(parseInt(competencyAssessmentId));
      fetchAssessment(parseInt(competencyAssessmentId));
    }
  }, [competencyAssessmentId]);

  // Start timer when questions are loaded
  useEffect(() => {
    if (candidateQuestions && candidateQuestions.length > 0) {
      // Get the time limit from the first question (all should have the same MinutesToComplete)
      const timeLimit = candidateQuestions[0]?.minutesToComplete || 30; // Default 30 minutes
      const totalSeconds = timeLimit * 60;
      setTotalTimeAllowed(totalSeconds);
      
      // Initialize localStorage with 0 if this is a fresh start (skip if already submitted to avoid recreating cleared keys)
      const storageKey = `quiz_total_time_${competencyAssessmentId}`;
      if (!isSubmittedRef.current && !localStorage.getItem(storageKey)) {
        localStorage.setItem(storageKey, '0');
        setTotalTimeSpent(0);
      }
      
      // Initialize form with competency assessment ID
      setValue('competencyAssessmentId', parseInt(competencyAssessmentId));
      setValue('candidateMinutesToComplete', 0);

      // Restore questionAnswers from saved answers if available
      const savedAnswersKey = `quiz_answers_${competencyAssessmentId}`;
      const savedAnswersStr = localStorage.getItem(savedAnswersKey);
      if (savedAnswersStr) {
        const savedAnswers = JSON.parse(savedAnswersStr);
        const restoredQuestionAnswers = Object.entries(savedAnswers).map(([questionId, answerId]) => {
          const question = candidateQuestions.find(q => q.questionId === parseInt(questionId));
          return {
            candidateQuestionAnswerId: question?.candidateQuestionAnswerId || 0,
            questionId: parseInt(questionId),
            answerId: answerId
          };
        });
        setValue('questionAnswers', restoredQuestionAnswers);
      } else {
        setValue('questionAnswers', []);
      }
      
      // Only start timer if quiz hasn't been submitted (prevents restart after fetchCandidateQuestions in handleSubmitQuiz)
      if (!isSubmittedRef.current) {
        startTimer();
      }
    }
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [candidateQuestions, totalTimeAllowed]);

  // Handle timer state flag when results are shown
  useEffect(() => {
    if (showResults && !resultsTimerHandled) {
      setResultsTimerHandled(true);
    }
  }, [showResults, resultsTimerHandled]);

  // Handle localStorage clearing when evaluation result is available
  useEffect(() => {
    if (evaluateAssessmentResult && showResults) {
      // Clear localStorage only for final results (not in-progress)
      if (evaluateAssessmentResult.assessmentResult && evaluateAssessmentResult.assessmentResult !== 'I') {
        clearQuizTotalTime();
      } 
    }
  }, [evaluateAssessmentResult, showResults]);

  // Reset timeout state when showing results
  useEffect(() => {
    if (showResults) {
      setIsTimedOut(false);
    }
  }, [showResults]);

  // Persist answers to localStorage
  useEffect(() => {
    const storageKey = `quiz_answers_${competencyAssessmentId}`;
    localStorage.setItem(storageKey, JSON.stringify(answers));
  }, [answers, competencyAssessmentId]);

  // Persist currentQuestionIndex to localStorage
  useEffect(() => {
    const storageKey = `quiz_question_index_${competencyAssessmentId}`;
    localStorage.setItem(storageKey, currentQuestionIndex.toString());
  }, [currentQuestionIndex, competencyAssessmentId]);

  // Persist isRetryMode to localStorage
  useEffect(() => {
    const storageKey = `quiz_retry_mode_${competencyAssessmentId}`;
    localStorage.setItem(storageKey, isRetryMode.toString());
  }, [isRetryMode, competencyAssessmentId]);

  const startTimer = () => {
    // Clear any existing timer
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    
    // Capture the base time from localStorage at session start
    const storageKey = `quiz_total_time_${competencyAssessmentId}`;
    const baseTime = parseInt(localStorage.getItem(storageKey) || '0', 10);
    const currentSessionStartTime = new Date();
    setIsTimerRunning(true);
    
    const interval = setInterval(() => {
      const now = new Date();
      const sessionElapsed = Math.floor((now - currentSessionStartTime) / 1000);
      const currentTotalTime = baseTime + sessionElapsed;
      
      setTotalTimeSpent(currentTotalTime);
      
      // Save to localStorage on every tick so it survives browser refresh
      localStorage.setItem(storageKey, currentTotalTime.toString());
      
      // Check if time has exceeded the allowed limit (only if not showing results and not already timed out)
      if (currentTotalTime >= totalTimeAllowed && !isTimedOut && !showResultsRef.current) {
        setIsTimedOut(true);
      }
    }, 1000);
    
    setTimerInterval(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setIsTimerRunning(false);
  };

  const clearQuizTotalTime = () => {
    const prefixes = ['quiz_total_time_', 'quiz_answers_', 'quiz_question_index_', 'quiz_retry_mode_'];
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (prefixes.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  const getTimeColor = (totalTimeSpent, totalTimeAllowed) => {
    if (totalTimeSpent > totalTimeAllowed) {
      return '#e74c3c'; // Red for overtime
    }
    
    const remainingTime = totalTimeAllowed - totalTimeSpent;
    
    if (remainingTime <= 60) {
      return '#e74c3c'; // Red for last minute
    }
    if (remainingTime <= 300) {
      return '#f39c12'; // Orange for last 5 minutes
    }
    return '#27ae60'; // Green for plenty of time
  };

  const formatTimeDisplay = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (answerId) => {
    const questions = getUniqueQuestions();
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion) {
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.questionId]: answerId
      }));
    }
  };

  const handleAnswerSelected = (questionId, answerId) => {
    // Update React Hook Form values
    const questions = getUniqueQuestions();
    const currentAnswers = getValues('questionAnswers') || [];
    
    // Find existing answer or create new one
    const existingIndex = currentAnswers.findIndex(qa => qa.questionId === questionId);
    const questionData = questions.find(q => q.questionId === questionId);
    
    const answerData = {
      candidateQuestionAnswerId: questionData?.candidateQuestionAnswerId || 0,
      questionId: questionId,
      answerId: answerId
    };
    
    if (existingIndex >= 0) {
      currentAnswers[existingIndex] = answerData;
    } else {
      currentAnswers.push(answerData);
    }
    
    setValue('questionAnswers', currentAnswers);
  };

  const handleNextQuestion = () => {
    // If timed out, auto-submit instead of navigating
    if (isTimedOut) {
      handleSubmitQuiz();
      return;
    }
    
    const questions = getUniqueQuestions();
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    // If timed out, auto-submit instead of navigating
    if (isTimedOut) {
      handleSubmitQuiz();
      return;
    }
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmitQuiz = async (data) => {
    // Build allQuestions from candidateQuestions (bypass retry mode filter) so passed answers keep their candidateQuestionAnswerId
    const allQuestionMap = new Map();
    candidateQuestions.forEach(item => {
      if (!allQuestionMap.has(item.questionId)) {
        allQuestionMap.set(item.questionId, {
          candidateQuestionAnswerId: item.candidateQuestionAnswerId,
          questionId: item.questionId,
        });
      }
    });
    const allQuestions = Array.from(allQuestionMap.values());

    // Build questionAnswers from answers state (reliable) instead of RHF accumulated values
    const questionAnswers = Object.entries(answers).map(([questionId, answerId]) => {
      const questionData = allQuestions.find(q => q.questionId === parseInt(questionId));
      return {
        candidateQuestionAnswerId: questionData?.candidateQuestionAnswerId || 0,
        questionId: parseInt(questionId),
        answerId: answerId
      };
    });

    // Validate all questions are answered before submission (skip validation on timeout)
    if (!isTimedOut && questionAnswers.length < allQuestions.length) {
      const unansweredCount = allQuestions.length - questionAnswers.length;
      alert(`Please answer all questions before submitting. You have ${unansweredCount} unanswered question${unansweredCount !== 1 ? 's' : ''}.`);
      return;
    }
    
    // Mark as submitted to prevent useEffect from restarting the timer after fetchCandidateQuestions
    isSubmittedRef.current = true;
    // FIRST: Stop the timer immediately to prevent it from overwriting localStorage
    stopTimer();
    // THEN: Capture the current time spent AFTER stopping timer
    const currentTimeSpent = totalTimeSpent;
    setResultsTimeSpent(currentTimeSpent);
    
    // IMMEDIATELY save the current time to localStorage
    const storageKey = `quiz_total_time_${competencyAssessmentId}`;
    localStorage.setItem(storageKey, currentTimeSpent.toString());
    
    // Calculate time spent in minutes
    const timeTakenMinutes = Math.ceil(currentTimeSpent / 60);
    
    // Prepare evaluation data
    const evaluationData = {
      competencyAssessmentId: parseInt(competencyAssessmentId),
      candidateMinutesToComplete: timeTakenMinutes,
      questionAnswers: questionAnswers
    };

    try {

      await evaluateCompetencyAssessment(evaluationData);
      
      // After successful evaluation, fetch updated results to get assessment results
      await fetchCandidateQuestions(parseInt(competencyAssessmentId));
      
      setShowResults(true);
      showResultsRef.current = true;
      const endTime = new Date();
      setQuizEndTime(endTime);
    } catch (error) {
      // console.error("Error submitting assessment:", error);
      // Still show results even if submission fails
      setShowResults(true);
      showResultsRef.current = true;
      clearQuizTotalTime();
    }
  };

  const handleRetryFailedQuestions = () => {
    // Get all questions (not filtered) to identify failed questions correctly
    
    const allQuestions = candidateQuestions.reduce((acc, item) => {
      if (!acc.some(q => q.questionId === item.questionId)) {
        acc.push({
          candidateQuestionAnswerId: item.candidateQuestionAnswerId,
          questionId: item.questionId,
          questionCode: item.questionCode,
          questionText: item.questionText,
          questionListOrder: item.questionListOrder,
          questionWeighting: item.questionWeighting,
          assessmentResult: item.assessmentResult,
          attemptNum: item.attemptNum,
          maxQuestionAttempts: item.maxQuestionAttempts,
          questionMediaUrl: item.questionMediaUrl,
          questionMediaImageContent: item.questionMediaImageContent,
          questionMediaImageExtension: item.questionMediaImageExtension,
          questionMediaLength: item.questionMediaLength,
          dlTrainingCompetencyQuestionAnswers: item.dlTrainingCompetencyQuestionAnswers || []
        });
      }
      return acc;
    }, []);

    const failedQuestionIds = allQuestions
      .filter(q => q.assessmentResult === 'F')
      .map(q => q.questionId);

    // Clear answers for failed questions from local state
    const updatedAnswers = { ...answers };
    failedQuestionIds.forEach(questionId => {
      delete updatedAnswers[questionId];
    });
    setAnswers(updatedAnswers);
    
    // Reset form for failed questions - keep only passed answers
    const currentQuestionAnswers = getValues('questionAnswers') || [];
    const passedAnswers = currentQuestionAnswers.filter(qa => 
      !failedQuestionIds.includes(qa.questionId)
    );
    setValue('questionAnswers', passedAnswers);
    
    // Enable retry mode to show only failed questions
    setIsRetryMode(true);
    
    // Navigate to first failed question (index 0 in filtered failed questions)
    setCurrentQuestionIndex(0);
    
    // Hide results and restart timer
    setShowResults(false);
    showResultsRef.current = false;
    setResultsTimerHandled(false); // Reset the timer handling flag
    
    // Reset submitted flag so timer can run again
    isSubmittedRef.current = false;
    // Restart the timer when retrying failed questions
    startTimer();
  };

  // Group questions by questionId to handle multiple candidate answers
  const getUniqueQuestions = () => {
    if (!candidateQuestions || candidateQuestions.length === 0) return [];
    
    const questionMap = new Map();
    
    candidateQuestions.forEach(item => {
      if (!questionMap.has(item.questionId)) {
        questionMap.set(item.questionId, {
          candidateQuestionAnswerId: item.candidateQuestionAnswerId,
          questionId: item.questionId,
          questionCode: item.questionCode,
          questionText: item.questionText,
          questionListOrder: item.questionListOrder,
          questionWeighting: item.questionWeighting,
          assessmentResult: item.assessmentResult, // Include assessment result for displaying results
          attemptNum: item.attemptNum, // Include current attempt number
          maxQuestionAttempts: item.maxQuestionAttempts, // Include max attempts allowed
          questionMediaUrl: item.questionMediaUrl,
          questionMediaImageContent: item.questionMediaImageContent,
          questionMediaImageExtension: item.questionMediaImageExtension,
          questionMediaLength: item.questionMediaLength,
          // Use the answer options from the DTO instead of reconstructing them
          dlTrainingCompetencyQuestionAnswers: item.dlTrainingCompetencyQuestionAnswers || []
        });
      }
    });
    
    let allQuestions = Array.from(questionMap.values()).sort((a, b) => 
      (a.questionListOrder || 0) - (b.questionListOrder || 0)
    );
    
    // If in retry mode, return only failed questions
    if (isRetryMode) {
      return allQuestions.filter(q => q.assessmentResult === 'F');
    }
    
    return allQuestions;
  };

  const questions = getUniqueQuestions();
  const currentQuestion = questions[currentQuestionIndex];

  if (candidateQuestionsLoading || evaluateAssessmentLoading || assessmentLoading) {
    return <SubmitLoader />;
  }

  if (candidateQuestionsError || evaluateAssessmentError || assessmentError) {
    return (
      <div className="quiz-error-container">
        <div className="quiz-error-content">
          <AlertMessage
            message={candidateQuestionsError || evaluateAssessmentError || assessmentError}
            type="error"
            className="quiz-error"
          />
          <button className="btn-back" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Assessment
          </button>
        </div>
      </div>
    );
  }

  if (!candidateQuestions || candidateQuestions.length === 0) {
    return (
      <div className="quiz-not-found">
        <h2>No Questions Available</h2>
        <p>There are no questions available for this assessment.</p>
        <button onClick={() => navigate(-1)} className="btn-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          Go Back
        </button>
      </div>
    );
  }

  if (showResults) {
    // console.log("Rendering results with evaluateAssessmentResult:", evaluateAssessmentResult);
    
    // Get all questions for results display (bypass retry mode filtering)
    const getAllQuestions = () => {
      if (!candidateQuestions || candidateQuestions.length === 0) return [];
      
      const questionMap = new Map();
      candidateQuestions.forEach(item => {
        if (!questionMap.has(item.questionId)) {
          questionMap.set(item.questionId, {
            candidateQuestionAnswerId: item.candidateQuestionAnswerId,
            questionId: item.questionId,
            questionCode: item.questionCode,
            questionText: item.questionText,
            questionListOrder: item.questionListOrder,
            questionWeighting: item.questionWeighting,
            assessmentResult: item.assessmentResult,
            attemptNum: item.attemptNum,
            maxQuestionAttempts: item.maxQuestionAttempts,
            questionMediaUrl: item.questionMediaUrl,
            questionMediaImageContent: item.questionMediaImageContent,
            questionMediaImageExtension: item.questionMediaImageExtension,
            questionMediaLength: item.questionMediaLength,
            dlTrainingCompetencyQuestionAnswers: item.dlTrainingCompetencyQuestionAnswers || []
          });
        }
      });
      
      return Array.from(questionMap.values()).sort((a, b) => 
        (a.questionListOrder || 0) - (b.questionListOrder || 0)
      );
    };
    
    const allQuestions = getAllQuestions();
    const currentQuestions = allQuestions; // Always show all questions in results (bypass retry mode filter)
    const totalQuestions = currentQuestions.length;
    const answeredQuestions = Object.keys(answers).length;
    const completionPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
    
    // Calculate total time taken in minutes using the captured time when results were first shown
    const totalTimeTaken = Math.ceil(resultsTimeSpent / 60);
    
    // Calculate results summary from current questions
    const passedQuestions = currentQuestions.filter(q => q.assessmentResult === 'P').length;
    const failedQuestions = currentQuestions.filter(q => q.assessmentResult === 'F').length;
    
    // Determine result status
    const isPass = evaluateAssessmentResult?.isPass || evaluateAssessmentResult?.assessmentResult === 'P';
    const isFail = evaluateAssessmentResult?.assessmentResult === 'F' || evaluateAssessmentResult?.assessmentResult === 'T';
    const isInProgress = evaluateAssessmentResult?.assessmentResult === 'I';

    // Get appropriate icon and title
    const getResultIcon = () => {
      if (isPass) return faTrophy;
      if (isFail) return faTimesCircle;
      return faExclamationTriangle; // Changed from faQuestionCircle to faExclamationTriangle
    };
    
    const getResultTitle = () => {
      if (isPass) return "Assessment Complete - Passed!";
      if (isFail) return "Assessment Complete - Failed";
      return "Assessment Submitted";
    };

    return (
      <div className="quiz-results-container">
        <div className={`results-header ${isPass ? 'success' : isFail ? 'failed' : 'in-progress'}`}>
          <h1>{getResultTitle()}</h1>
          <FontAwesomeIcon icon={getResultIcon()} className="trophy-icon" />
          <p>{evaluateAssessmentResult?.message || "Your assessment has been submitted successfully."}</p>
        </div>

        <div className="results-summary">
          <div className="result-card">
            <FontAwesomeIcon icon={faCheckCircle} className="result-icon success" />
            <h3>Questions Passed</h3>
            <p>{passedQuestions} of {totalQuestions}</p>
          </div>
          
          <div className="result-card">
            <FontAwesomeIcon icon={faCalendarAlt} className="result-icon info" />
            <h3>Time Taken</h3>
            <p>
              {totalTimeTaken >= 60
                ? `${Math.floor(totalTimeTaken / 60)} hour${Math.floor(totalTimeTaken / 60) !== 1 ? 's' : ''}${totalTimeTaken % 60 > 0 ? ` ${totalTimeTaken % 60} minute${totalTimeTaken % 60 !== 1 ? 's' : ''}` : ''}`
                : `${totalTimeTaken} minute${totalTimeTaken !== 1 ? 's' : ''}`}
            </p>
          </div>
          
          <div className="result-card">
            <FontAwesomeIcon icon={faQuestionCircle} className="result-icon primary" />
            <h3>Success Rate</h3>
            <p>{totalQuestions > 0 ? ((passedQuestions / totalQuestions) * 100).toFixed(1) : 0}%</p>
          </div>
        </div>

        {/* Detailed Results */}
        <div className="detailed-results">
          <h3>Question Results</h3>
          <div className="question-results-list">
            {currentQuestions.map((question, index) => (
              <div key={question.questionId} className={`question-result-item ${question.assessmentResult === 'P' ? 'passed' : 'failed'}`}>
                <div className="question-info">
                  <span className="question-number">Q{index + 1}</span>
                  <span className="question-text">{question.questionText}</span>
                </div>
                <div className="result-status">
                  <FontAwesomeIcon 
                    icon={question.assessmentResult === 'P' ? faCheckCircle : faTimesCircle} 
                    className={question.assessmentResult === 'P' ? 'success' : 'error'}
                  />
                  <span className="result-text">
                    {question.assessmentResult === 'P' ? 'Pass' : 'Fail'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="results-actions">
          {isInProgress ? (
            <button 
              onClick={handleRetryFailedQuestions}
              className="btn-primary retry-btn"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Retry Failed Questions
            </button>
          ) : (
            <button 
              onClick={() => {
                // Navigate back to course detail page using courseEnrollmentId from the assessment
                const enrolmentId = currentAssessment?.courseEnrollmentId;
                if (enrolmentId && !evaluateAssessmentResult?.isFinalAttempt) {
                  navigate(`/trainingcourses/enrollment/${enrolmentId}`);
                } else {
                  navigate('/trainingcourses');
                }
              }}
              className="btn-secondary"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Course Details
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      {/* Quiz Header */}
      <div className="quiz-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Assessment
        </button>
        
        <div className="quiz-title">
          <h1>{isRetryMode ? 'Retry Failed Questions' : 'Competency Assessment'}</h1>
          <p>{isRetryMode ? 'Answer the failed questions to complete your assessment' : 'Answer each question within the time limit'}</p>
          
          {/* Quiz Progress Information */}
          <div className="quiz-progress-info">
            <div className="progress-stats">
              <span className="total-time-spent">
                <FontAwesomeIcon icon={faClock} />
                Time: <span style={{ color: getTimeColor(totalTimeSpent, totalTimeAllowed) }}>{formatTimeDisplay(totalTimeSpent)}</span> / {formatTimeDisplay(totalTimeAllowed)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Question Display */}
      {currentQuestion && (
        <form onSubmit={handleSubmit(handleSubmitQuiz)}>
          <QuestionDisplay
            question={currentQuestion}
            questionIndex={currentQuestionIndex}
            totalQuestions={questions.length}
            selectedAnswer={answers[currentQuestion.questionId]}
            onAnswerChange={handleAnswerChange}
            onNext={handleNextQuestion}
            onPrevious={handlePreviousQuestion}
            onSubmit={handleSubmit(handleSubmitQuiz)}
            isFirstQuestion={currentQuestionIndex === 0}
            isLastQuestion={currentQuestionIndex === questions.length - 1}
            onAnswerSelected={handleAnswerSelected}
            isTimedOut={isTimedOut}
          />
        </form>
      )}

      {/* Quiz Footer */}
      <div className="quiz-footer">
        <div className="quiz-info">
          <span>Assessment started at {quizStartTime.toLocaleTimeString()}</span>
          <span>•</span>
          <span>{questions.length} total questions</span>
        </div>
      </div>
    </div>
  );
};

export default CompetencyQuiz;